import {Component, EventEmitter, Input, Output} from '@angular/core';
import {addMonths, subMonths, format, getDayOfYear} from 'date-fns';
import {fr} from 'date-fns/locale';
import {LoaderService, LangService} from 'ngx-satoris';

@Component({
  selector: 'app-place-opening-setting',
  templateUrl: './place-opening-setting.component.html',
  styleUrls: ['./place-opening-setting.component.scss']
})
export class PlaceOpeningSettingComponent {
  @Input() openingDays: number[] = [];
  @Input() openingHours: { from: number; to: number, day?: number }[] = [{from: 9, to: 17}];
  @Input() closedDays: number[] = [];
  @Input() modeLocal: 'open' | 'closed' = 'closed';

  @Output() modeLocalChange = new EventEmitter<'open' | 'closed'>();
  @Output() change = new EventEmitter<any>();

  readonly daysOfWeek = [
    {id: 1, key: 'monday'},
    {id: 2, key: 'tuesday'},
    {id: 3, key: 'wednesday'},
    {id: 4, key: 'thursday'},
    {id: 5, key: 'friday'},
    {id: 6, key: 'saturday'},
    {id: 0, key: 'sunday'}
  ];

  currentMonth = new Date();

  constructor(private loader: LoaderService, private lang: LangService) {}

  ngOnInit(): void {
    if(this.openingHours && Array.isArray(this.openingHours)) {
      this.openingHours.sort((a, b) => {
        const au = a.day === undefined;
        const bu = b.day === undefined;
        if(au && !bu) return -1; 
        if(!au && bu) return 1;  
        return 0;
      });
    }
  }

  ngOnChanges(): void {
    this.initMode();
  }

  private initMode(): void {
    const year = new Date().getFullYear();

    const isProbablyClosed = (this.openingDays.length === 0);

    if(isProbablyClosed) {
      this.modeLocal = 'closed';
      this.openingDays = [];
      if(this.closedDays.length === 0) {
        for(let day = 1; day <= 366; day++) {
          const date = new Date(year, 0, day);
          if(date.getFullYear() === year) this.closedDays.push(day);
        }
      }
    } else {
      this.modeLocal = 'open';
      this.closedDays = this.closedDays.filter((d) => !isNaN(d));
    }
  }

  confirmModeChange(newMode: 'open' | 'closed'): void {
    if(this.modeLocal === newMode) return;

    this.loader.loading(true, {type: 'question', message: this.lang.transform('calendar.confirmReset'), btnLabel: 'global.yes', custom: {icon: 'trash'}}).then((res: boolean) => {
      if(!res) return;
      this.modeLocal = newMode;
      this.modeLocalChange.emit(newMode);
      this.openingHours = [{from: 9, to: 17}];
      const year = new Date().getFullYear();
      const daysInYear = (new Date(year + 1, 0, 1).getTime() - new Date(year, 0, 1).getTime()) / 86400000;   
      this.closedDays = [];

      if(newMode === 'open') {
        this.openingDays = [1, 2, 3, 4, 5];
      } else {
        this.openingDays = [];
        for(let day = 1; day <= daysInYear; day++) {
          const date = new Date(year, 0, day);
          if(date.getFullYear() === year) this.closedDays.push(day);
        }
      }
      this.emit();
    });
  }

  get monthDays(): Date[] {
    const start = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    const end = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);
    const days: Date[] = [];
    for(let d = start; d <= end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  }

  get monthCells(): (Date | null)[] {
    const first = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    return Array(offset).fill(null).concat(this.monthDays);
  }

  changeMonth(delta: number): void {
    this.currentMonth = delta > 0 ? addMonths(this.currentMonth, 1) : subMonths(this.currentMonth, 1);
  }

  toggleCalendarDay(date: Date): void {
    const dayNum = getDayOfYear(date);
    const isSelected = this.closedDays.includes(dayNum);

    if(isSelected) {
      this.closedDays = this.closedDays.filter(d => d !== dayNum);
    } else {
      this.closedDays.push(dayNum);
    }

    this.emit();
  }

  toggleWeekDay(dayId: number): void {
    if(this.modeLocal !== 'open') return;

    const year = new Date().getFullYear();
    const isCurrentlyOpen = this.openingDays.includes(dayId);

    if(isCurrentlyOpen) {
      this.openingDays = this.openingDays.filter(d => d !== dayId);
      const toAdd: number[] = [];
      for(let day = 1; day <= 366; day++) {
        const date = new Date(year, 0, day);
        if(date.getFullYear() === year && date.getDay() === dayId) {
          toAdd.push(day);
        }
      }
      const set = new Set(this.closedDays.concat(toAdd));
      this.closedDays = Array.from(set);
    } else {
      this.openingDays = [...this.openingDays, dayId];
      this.closedDays = this.closedDays.filter(dayNum => {
        const date = new Date(year, 0, dayNum);
        return date.getDay() !== dayId;
      });
    }

    this.openingDays = [...this.openingDays];
    this.closedDays  = [...this.closedDays];
    this.currentMonth = new Date(this.currentMonth);

    this.emit();
  }


  addHourRange(): void {
    this.openingHours.push({from: 9, to: 17});
    this.emit();
  }

  removeHourRange(i: number): void {
    if(this.openingHours.length > 1) {
      this.openingHours.splice(i, 1);
      this.emit();
    }
  }

  updateHourRange(i: number, key: 'from' | 'to' | 'day', value: number | undefined): void {
    if(!this.openingHours[i]) return;

    let num = Number(value);
    if(isNaN(num)) num = 0;
    num = Math.max(0, Math.min(23, num));

    const current = {...this.openingHours[i]};
    current[key] = num;

    if(key !== 'day') {
      if(current.to <= current.from) {
        current.to = Math.min(current.from + 1, 23);
      }
    }

    this.openingHours = this.openingHours.map((h, idx) => (idx === i ? current : h));
    this.emit();
  }

  isOpen(day: Date): boolean {
    const dayOfWeek = day.getDay();
    const dayNum = getDayOfYear(day);

    if(this.modeLocal === 'open') {
      return this.openingDays.includes(dayOfWeek) && !this.closedDays.includes(dayNum);
    } else {
      return !this.closedDays.includes(dayNum);
    }
  }

  formatMonth(): string {
    return format(this.currentMonth, 'MMMM yyyy', {locale: fr});
  }

  private emit(): void {
    this.closedDays = (this.closedDays || []).filter((v): v is number => typeof v === 'number' && !isNaN(v));
    this.openingDays = (this.openingDays || []).filter((v): v is number => typeof v === 'number' && !isNaN(v));

    this.change.emit({
      openingDays: this.openingDays,
      openingHours: this.openingHours,
      closedDays: this.closedDays
    });
  }

  onModeClick(event: MouseEvent, newMode: 'open' | 'closed'): void {
    if(this.modeLocal === newMode) return;
    event.preventDefault();
    this.confirmModeChange(newMode);
  }
}